import React from 'react';
import { Crown, Sparkles, X, Check, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/Button';

interface QuotaExceededModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenTopUp?: () => void;
  title?: string;
  message?: string;
  type?: 'set' | 'question' | 'general';
  currentTier?: 'USER' | 'VIP' | 'SVIP' | number;
}

export const QuotaExceededModal: React.FC<QuotaExceededModalProps> = ({
  isOpen,
  onClose,
  onOpenTopUp,
  title = 'Đã đạt giới hạn hạn mức gói',
  message,
  type = 'general',
  currentTier = 'USER'
}) => {
  if (!isOpen) return null;

  const tierLabel = typeof currentTier === 'number' 
    ? (currentTier === 2 ? 'SVIP' : currentTier === 1 ? 'VIP' : 'USER')
    : (currentTier || 'USER');

  const defaultMsg = type === 'set'
    ? `Tài khoản ${tierLabel} của Thầy/Cô đã đạt giới hạn tối đa số lượng bộ câu hỏi cho phép. Vui lòng nâng cấp VIP/SVIP để tạo thêm không giới hạn!`
    : type === 'question'
    ? `Bộ câu hỏi này đã đạt giới hạn số lượng câu hỏi tối đa của tài khoản ${tierLabel}. Vui lòng nâng cấp VIP/SVIP để mở rộng dung lượng chứa câu hỏi!`
    : `Thao tác vượt quá hạn mức tối đa của tài khoản ${tierLabel}. Vui lòng nâng cấp tài khoản để tiếp tục.`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl border border-amber-200 relative overflow-hidden text-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Background decorative gradient */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-400" />
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-amber-100 via-amber-50 to-orange-100 text-amber-600 flex items-center justify-center mx-auto mb-4 border border-amber-200/80 shadow-inner">
          <Crown className="w-8 h-8 text-amber-600 animate-pulse" />
        </div>

        {/* Title & Tier Badge */}
        <div className="inline-flex items-center space-x-1.5 px-3 py-1 bg-amber-50 text-amber-800 text-xs font-bold rounded-full border border-amber-200 mb-2.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-600" />
          <span>Tài khoản hiện tại: {tierLabel}</span>
        </div>

        <h3 className="text-xl font-black text-slate-900 tracking-tight mb-2">
          {title}
        </h3>

        {/* Message */}
        <div className="bg-amber-50/70 border border-amber-200/80 rounded-2xl p-4 text-xs sm:text-sm text-amber-950 leading-relaxed mb-5 text-left">
          <p className="font-medium">
            {message || defaultMsg}
          </p>
        </div>

        {/* Plan Quota Comparision Table */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 mb-6 text-left space-y-2">
          <p className="text-[11px] font-black uppercase text-slate-500 tracking-wider">
            Bảng hạn mức Ngân hàng câu hỏi:
          </p>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className={`p-2.5 rounded-xl border ${tierLabel === 'USER' ? 'bg-amber-100/70 border-amber-300 font-bold' : 'bg-white border-slate-200'}`}>
              <div className="text-[10px] text-slate-500 uppercase font-semibold">USER</div>
              <div className="text-slate-900 font-bold mt-0.5">10 bộ</div>
              <div className="text-[10px] text-slate-500">100 câu/bộ</div>
            </div>

            <div className={`p-2.5 rounded-xl border ${tierLabel === 'VIP' ? 'bg-amber-100/70 border-amber-300 font-bold' : 'bg-white border-slate-200'}`}>
              <div className="text-[10px] text-amber-700 uppercase font-bold flex items-center justify-center space-x-0.5">
                <Crown className="w-2.5 h-2.5" />
                <span>VIP</span>
              </div>
              <div className="text-slate-900 font-bold mt-0.5">20 bộ</div>
              <div className="text-[10px] text-slate-500">200 câu/bộ</div>
            </div>

            <div className={`p-2.5 rounded-xl border ${tierLabel === 'SVIP' ? 'bg-purple-100/70 border-purple-300 font-bold' : 'bg-white border-slate-200'}`}>
              <div className="text-[10px] text-purple-700 uppercase font-bold flex items-center justify-center space-x-0.5">
                <Sparkles className="w-2.5 h-2.5" />
                <span>SVIP</span>
              </div>
              <div className="text-slate-900 font-bold mt-0.5">40 bộ</div>
              <div className="text-[10px] text-slate-500">300 câu/bộ</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full sm:w-auto text-xs font-semibold text-slate-600 hover:text-slate-900 order-2 sm:order-1"
          >
            Đóng
          </Button>

          {onOpenTopUp && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenTopUp();
              }}
              className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 hover:from-amber-600 hover:to-amber-700 text-white font-black text-xs sm:text-sm rounded-xl shadow-md hover:shadow-lg transition-all active:scale-95 flex items-center justify-center space-x-1.5 cursor-pointer order-1 sm:order-2 border border-amber-300/60"
            >
              <Crown className="w-4 h-4 text-yellow-200" />
              <span>Nâng cấp VIP ngay</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
